/**
 * @author      tae@zumper.com (Tae Yoon)
 * @copyright   Copyright (c) 2018, Zumper
 * @description router selectors
 */

import { LEASE_LENGTH_SELECTIONS } from '@zcode-ui/filters'
import { memoizeMaybeRouteProps } from '@zcode/memoizers'
import { memoizeRouteProps } from '@zcode/memoizers'
import { getQueryBounds, isValidBounds } from '@zcode/nr-utils'
import { routesConstants } from '@zcode/zumper-routes'
import { compose } from 'redux'
import { default as matchLocationPath } from '../../../components/main/Rail/CurrentSearchView/helpers/matchLocationPath'
import { checkMainView } from '../../../inspectors/index'
import { PROPERTY_TYPE_PATH_RE } from '../../../utils/urls/constants'

/**
 *
 * @template {{ [K in keyof Params]?: string; }} Params
 * @template S
 * @param {Pick<import('react-router-dom').RouteChildrenProps<Params, S>, 'location'>} routeProps
 * @returns
 */
export const selectLocation = (routeProps) => routeProps?.location

/**
 *
 * @param {import('react-router-dom').RouteChildrenProps<{ [K in keyof Params]?: string | undefined }>} routeProps
 * @returns
 */
export const selectMatch = (routeProps) => routeProps?.match

export const selectParams = compose((match) => match?.params, selectMatch)

export const selectPathname = compose((location) => location?.pathname, selectLocation)

const hasBoxSearch = /[&?]box=/
const matchBoxSearch = /[&?]box=([-\d.,]*?)(?:&.*)?$/

export const selectBox = memoizeRouteProps((routeProps) => {
  if (!routeProps) {
    return false
  }

  const location = selectLocation(routeProps)
  if (location && location.search && location.search.search(hasBoxSearch) > -1) {
    const matches = location.search.match(matchBoxSearch)
    if (matches) {
      const [, boxString] = matches
      const box = getQueryBounds(boxString)
      if (isValidBounds(box)) {
        return box
      }
    }
  }
  return null
})

export const selectHasBox = memoizeMaybeRouteProps((routeProps) => {
  if (!routeProps) {
    return false
  }
  return !!selectBox(routeProps)
})

// creates a filter string minus the box search
export const selectFilterString = memoizeRouteProps((routeProps) => {
  if (!routeProps) {
    return ''
  }

  const location = selectLocation(routeProps)
  const params = selectParams(routeProps)

  if (params && location) {
    const filtersPath = params.filtersPath || ''
    const search = location.search.replace(/&?box=[-\d.,]*?(&.*)?$/, '$1')
    return `${filtersPath}${search !== '?' ? search : ''}`
  }
})

export const selectFiltersPath = memoizeRouteProps((routeProps) => {
  if (!routeProps) {
    return ''
  }

  const params = selectParams(routeProps)
  return params?.filtersPath || ''
})

export const selectSearch = compose(
  (location) => (location ? location.search : null),
  selectLocation
)

export const selectFiltersPathBeds = memoizeRouteProps((routeProps) => {
  const filtersPath = selectFiltersPath(routeProps)
  if (filtersPath === '/studios') {
    return 0
  } else if (filtersPath === '/1-beds') {
    return 1
  } else if (filtersPath === '/2-beds') {
    return 2
  } else if (filtersPath === '/3-beds') {
    return 3
  } else if (filtersPath === '/4-beds') {
    return 4
  }
  return null
})

export const selectPage = compose((search) => {
  const params = new URLSearchParams(search)
  return Number(params.get('page')) || 1
}, selectSearch)

/**
 * @type {(routeProps: Pick<import('react-router').RouteChildrenProps, 'location'>) => string}
 */
export const selectHash = compose((location) => location?.hash, selectLocation)

/** @param {import('react-router-dom').RouteComponentProps} routeProps */
/** @returns {import('../../currentSearch/helpers').ICPropertyTypePath} */
export const getBasePath = memoizeRouteProps((routeProps) => {
  const path = routeProps.location && routeProps.location.pathname
  const propertyTypePath = PROPERTY_TYPE_PATH_RE.exec(path)

  return propertyTypePath
    ? /** @type {'apartments-for-rent' | 'condos-for-rent' | 'houses-for-rent' | 'rooms-for-rent'} */ (
        propertyTypePath[0]
      )
    : routesConstants.APTS_BASE_URL
})

export const getMatchPath = memoizeRouteProps((routeProps) => {
  return routeProps.match && routeProps.match.params.filtersPath
})

export const getPropertyTypeText = (routeProps) => {
  const basePath = getBasePath(routeProps)
  const leaseLength = selectLeaseLength(routeProps)
  /** @type {'condos' | 'houses' | 'rooms' | 'apartments' | 'monthly rentals' | 'rentals'} */
  let propertyTypeText

  if (basePath === routesConstants.CONDOS_BASE_URL) {
    propertyTypeText = 'condos'
  } else if (basePath === routesConstants.HOUSES_BASE_URL) {
    propertyTypeText = 'houses'
  } else if (basePath === routesConstants.ROOMS_BASE_URL) {
    propertyTypeText = 'rooms'
  } else if (basePath === routesConstants.APTS_BASE_URL) {
    propertyTypeText = 'apartments'
  } else {
    if (leaseLength === LEASE_LENGTH_SELECTIONS.SHORT_TERM) {
      propertyTypeText = 'monthly rentals'
    } else {
      propertyTypeText = 'rentals'
    }
  }

  return propertyTypeText
}

export const selectIsAFR = (routeProps) => {
  const basePath = getBasePath(routeProps)
  return basePath === routesConstants.APTS_BASE_URL
}

export const selectIsHFR = (routeProps) => {
  const basePath = getBasePath(routeProps)
  return basePath === routesConstants.HOUSES_BASE_URL
}

export const selectIsRFR = (routeProps) => {
  const basePath = getBasePath(routeProps)
  return basePath === routesConstants.ROOMS_BASE_URL
}

export const selectIsCFR = (routeProps) => {
  const basePath = getBasePath(routeProps)
  return basePath === routesConstants.CONDOS_BASE_URL
}

// SWS experiment
const excludedStates = new Set([
  'il',
  'ny',
  // no canadian provinces
  'on',
  'qc',
  'ns',
  'nb',
  'mb',
  'bc',
  'pe',
  'sk',
  'ab',
  'nl',
  'nt',
  'yt',
  'nu',
])
export const selectSwsEnabled = memoizeRouteProps((routeProps) => {
  const location = selectLocation(routeProps)
  const isMainView = checkMainView({ location })
  if (!isMainView) {
    return false
  }
  const params = selectParams(routeProps)
  if (!params?.locationPath) {
    return false
  }
  const { statePath } = matchLocationPath(params?.locationPath)
  return !!statePath && !excludedStates.has(statePath)
})

const canadianProvinces = new Set([
  'on',
  'qc',
  'ns',
  'nb',
  'mb',
  'bc',
  'pe',
  'sk',
  'ab',
  'nl',
  'nt',
  'yt',
  'nu',
])
export const selectIsCanadianProvince = memoizeRouteProps((routeProps) => {
  const location = selectLocation(routeProps)
  const isMainView = checkMainView({ location })
  if (!isMainView) {
    return false
  }
  const params = selectParams(routeProps)
  if (!params?.locationPath) {
    return false
  }
  const { statePath } = matchLocationPath(params?.locationPath)
  return !!statePath && !!canadianProvinces.has(statePath)
})

export const selectLeaseLength = memoizeRouteProps((routeProps) => {
  const { search } = selectLocation(routeProps) || {}
  const searchParams = new URLSearchParams(search)
  let leaseLength = ''
  if (!searchParams || !searchParams.has('lease-term')) {
    leaseLength = LEASE_LENGTH_SELECTIONS.LONG_TERM
  } else if (searchParams.has('lease-term') && searchParams.get('lease-term') === 'short') {
    leaseLength = LEASE_LENGTH_SELECTIONS.SHORT_TERM
  }
  return leaseLength
})
